import { memo, useCallback, useState } from "react";
import {
  VStack,
} from "@chakra-ui/react";
import AlertCard from "./AlertCard";

interface Alert {
  id: string;
  name: string;
  type?: string;
  severity: string;
  status: string;
  message: string;
  firedAt: string;
}

interface AlertsListProps {
  alerts: Alert[];
  totalCount: number;
  onAlertStatusUpdate?: (alertId: string, newStatus: string) => void;
  refetchQueries?: string[];
  maxHeight?: string;
  updateNote?: string;
  canResolveAlerts?: boolean;
  currentAlertLink?: string;
}

function AlertsList({
  alerts,
  totalCount,
  onAlertStatusUpdate,
  refetchQueries = [],
  maxHeight = "400px",
  updateNote = "Updated from alerts list",
  canResolveAlerts = true,
  currentAlertLink: _currentAlertLink = undefined
}: AlertsListProps) {
  const [resolvingAlertId, _setResolvingAlertId] = useState<string | null>(null);

  const handleAlertStatusUpdate = useCallback(
    (alertId: string, newStatus: string) => {
      if (onAlertStatusUpdate) {
        onAlertStatusUpdate(alertId, newStatus);
      }
    },
    [onAlertStatusUpdate]
  );

  // Don't render anything if no alerts
  if (!alerts.length || totalCount === 0) {
    return null;
  }

  const isScrollable = maxHeight && maxHeight !== "none";

  return (
    <VStack
      align="stretch"
      width="100%"
      gap={2}
      maxHeight={isScrollable ? maxHeight : undefined}
      overflowY={isScrollable ? "auto" : "visible"}
      overflowX="hidden"
      css={
        isScrollable
          ? {
              "&::-webkit-scrollbar": { width: "0.375rem" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                borderRadius: "0.1875rem",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
            }
          : undefined
      }
    >
      {alerts.map((alert) => (
        <AlertCard
          key={alert.id}
          alert={alert}
          onStatusUpdate={handleAlertStatusUpdate}
          refetchQueries={refetchQueries}
          updateNote={updateNote}
          isUpdating={resolvingAlertId === alert.id}
          canResolveAlert={canResolveAlerts}
        />
      ))}
    </VStack>
  );
}

export default memo(AlertsList, (prev, next) => {
  if (prev.alerts === next.alerts) return true;
  if (prev.alerts.length !== next.alerts.length) return false;
  return prev.alerts.every(
    (a, i) => a.id === next.alerts[i]?.id && a.message === next.alerts[i]?.message
  );
});